/**
 * Who may do what to an announcement, and how each one is labelled.
 *
 * The rule that matters: **the creator cannot approve their own item** — unless they hold
 * `whats_new:approve_own`, which waives it. The BFF enforces both halves, and the UI shows an
 * explicit "Outro aprovador" marker rather than hiding the buttons, so the person understands why
 * they cannot act instead of thinking the screen is broken.
 *
 * @module Pages/BUs/WhatsNew
 */

import type { WhatsNew } from '../Types/WhatsNew.types';
import { WhatsNewApprovalStatus } from '../Types/WhatsNew.types';
import type { Status } from '@/Shared/components/StatusTag/StatusTag';

/**
 * The BFF permission that waives the four-eyes rule.
 *
 * Spelled out here rather than inlined at the call site so the string lives next to the rule it
 * changes — it comes from the BFF, not from Flagsmith, and the two are easy to confuse.
 */
export const WHATS_NEW_APPROVE_OWN = 'whats_new:approve_own';

/**
 * The BFF permission that allows undoing someone's "seen" record.
 *
 * Held by `super_admin` alone: it reaches another person's data, not the announcement's.
 */
export const WHATS_NEW_RESET_VIEWER = 'whats_new:reset_viewer';

/** Flags that gate the actions on a card. */
export interface WhatsNewPermissions {
  submit: boolean;
  approve: boolean;
  edit: boolean;
  delete: boolean;
  /**
   * Whether this person may waive the four-eyes rule on their own submissions.
   *
   * Unlike the others, this one is not a Flagsmith flag: it is the BFF permission
   * `whats_new:approve_own`, held by `super_admin` alone. It is additional to `approve`,
   * never a replacement — the BFF requires both.
   */
  approveOwn: boolean;
}

/** What a card may offer for one announcement. */
export interface WhatsNewActions {
  /** Send a draft for approval. */
  canSubmit: boolean;
  /** Approve or reject someone else's pending item. */
  canApprove: boolean;
  /**
   * Pending, it is the viewer's own item, and they cannot waive the rule — show the "another
   * approver" marker.
   */
  isBlockedFromApproving: boolean;
  /** Drafts and rejected items go back into the creation flow. */
  canEdit: boolean;
  /**
   * Remove the announcement. The BFF soft-deletes it (`deletedAt` + `isActive: false`) and allows
   * it in any status, but there is no restore endpoint — so from the panel it is one-way.
   */
  canDelete: boolean;
  /** Reach exists only once approved. */
  hasReach: boolean;
}

/**
 * Resolves the actions for one announcement.
 *
 * @param item - The announcement.
 * @param currentUserId - The signed-in user, to detect self-approval.
 * @param permissions - The viewer's feature flags.
 */
export function getWhatsNewActions(
  item: WhatsNew,
  currentUserId: string | undefined,
  permissions: WhatsNewPermissions,
): WhatsNewActions {
  const isDraft = item.approvalStatus === WhatsNewApprovalStatus.DRAFT;
  const isPending = item.approvalStatus === WhatsNewApprovalStatus.PENDING_APPROVAL;
  const isRejected = item.approvalStatus === WhatsNewApprovalStatus.REJECTED;
  const isApproved = item.approvalStatus === WhatsNewApprovalStatus.APPROVED;

  const isOwnItem = currentUserId !== undefined && item.createdBy?.id === currentUserId;

  const canWaiveOwn = isOwnItem && permissions.approveOwn;

  return {
    canSubmit: isDraft && permissions.submit,
    canApprove: isPending && permissions.approve && (!isOwnItem || canWaiveOwn),
    isBlockedFromApproving: isPending && permissions.approve && isOwnItem && !canWaiveOwn,
    canEdit: (isDraft || isRejected) && permissions.edit,
    canDelete: permissions.delete,
    hasReach: isApproved,
  };
}

/** The four buckets the list groups announcements into. */
export const WHATS_NEW_BUCKETS = [
  { id: 'all', label: 'Todos' },
  { id: WhatsNewApprovalStatus.PENDING_APPROVAL, label: 'Aguardando' },
  { id: WhatsNewApprovalStatus.DRAFT, label: 'Rascunho' },
  { id: WhatsNewApprovalStatus.APPROVED, label: 'Aprovado' },
  { id: WhatsNewApprovalStatus.REJECTED, label: 'Rejeitado' },
] as const;

const STATUS_PRESENTATION: Record<WhatsNewApprovalStatus, { status: Status; label: string }> = {
  [WhatsNewApprovalStatus.PENDING_APPROVAL]: { status: 'pending', label: 'Aguardando' },
  [WhatsNewApprovalStatus.DRAFT]: { status: 'neutral', label: 'Rascunho' },
  [WhatsNewApprovalStatus.APPROVED]: { status: 'success', label: 'Aprovado' },
  [WhatsNewApprovalStatus.REJECTED]: { status: 'danger', label: 'Rejeitado' },
};

/** Pill color and label for an announcement's status. */
export function getStatusPresentation(status: WhatsNewApprovalStatus): {
  status: Status;
  label: string;
} {
  return STATUS_PRESENTATION[status];
}

/**
 * The "Rejeitado por X: motivo" line.
 *
 * The BFF stores the reason but does not expose who rejected it as its own field, so the approver
 * relation is used when present; otherwise the line names no one rather than inventing a name.
 * Returns `undefined` when there is no reason to show.
 */
export function getRejectionLine(item: WhatsNew): string | undefined {
  if (item.approvalStatus !== WhatsNewApprovalStatus.REJECTED) return undefined;
  if (!item.rejectionReason) return undefined;

  const author = item.approvedBy?.name;
  return author
    ? `Rejeitado por ${author}: ${item.rejectionReason}`
    : `Rejeitado: ${item.rejectionReason}`;
}
